import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  CvDocument,
  archiveDocument,
  listDocuments,
} from "features/cv-processor/api/CvProcessorApi";

interface CvProcessorContextValue {
  docs: CvDocument[];
  docsLoading: boolean;
  docsError: string;
  activeDoc: CvDocument | null;
  view: "list" | "editor";
  showNewModal: boolean;
  archiving: number | null;
  pendingArchive: CvDocument | null;
  loadDocs: () => Promise<void>;
  openDoc: (doc: CvDocument) => void;
  backToList: () => void;
  handleCreate: (doc: CvDocument) => void;
  requestArchive: (doc: CvDocument, e: React.MouseEvent) => void;
  confirmArchive: () => Promise<void>;
  cancelArchive: () => void;
  setShowNewModal: (show: boolean) => void;
}

const CvProcessorContext = createContext<CvProcessorContextValue | null>(null);

export function CvProcessorProvider({ children }: { children: React.ReactNode }) {
  const [docs, setDocs] = useState<CvDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [docsError, setDocsError] = useState("");
  const [activeDoc, setActiveDoc] = useState<CvDocument | null>(null);
  const [view, setView] = useState<"list" | "editor">("list");
  const [showNewModal, setShowNewModal] = useState(false);
  const [archiving, setArchiving] = useState<number | null>(null);
  const [pendingArchive, setPendingArchive] = useState<CvDocument | null>(null);

  const loadDocs = useCallback(async () => {
    setDocsError("");
    try {
      const data = await listDocuments();
      setDocs(data);
    } catch (e: any) {
      setDocsError(e.response?.data?.detail || "Failed to load documents");
    } finally {
      setDocsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const openDoc = (doc: CvDocument) => {
    setActiveDoc(doc);
    setView("editor");
  };

  const backToList = () => {
    setView("list");
    setActiveDoc(null);
    // Refresh — a create or a category edit in the editor only updated local
    // state, not this list, so "already used" category checks elsewhere
    // (New CV modal, other editors) would otherwise see a stale snapshot.
    loadDocs();
  };

  const handleCreate = (doc: CvDocument) => {
    setShowNewModal(false);
    openDoc(doc);
  };

  const requestArchive = (doc: CvDocument, e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingArchive(doc);
  };

  const cancelArchive = () => setPendingArchive(null);

  const confirmArchive = async () => {
    const doc = pendingArchive;
    if (!doc) return;
    setArchiving(doc.id);
    try {
      await archiveDocument(doc.id);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (e: any) {
      setDocsError(e.response?.data?.detail || "Could not archive document");
    } finally {
      setArchiving(null);
      setPendingArchive(null);
    }
  };

  return (
    <CvProcessorContext.Provider
      value={{
        docs,
        docsLoading,
        docsError,
        activeDoc,
        view,
        showNewModal,
        archiving,
        pendingArchive,
        loadDocs,
        openDoc,
        backToList,
        handleCreate,
        requestArchive,
        confirmArchive,
        cancelArchive,
        setShowNewModal,
      }}
    >
      {children}
    </CvProcessorContext.Provider>
  );
}

export function useCvProcessor(): CvProcessorContextValue {
  const ctx = useContext(CvProcessorContext);
  if (!ctx) throw new Error("useCvProcessor must be used inside CvProcessorProvider");
  return ctx;
}
